import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Snackbar,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItemButton,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Alert,
  FormControl,
  FormLabel,
  MenuItem,
  useMediaQuery,
  useTheme,
  Select,
} from "@mui/material";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import { DateRangeOutlined, List as ListIcon } from "@mui/icons-material";
import {
  Today as TodayIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as CompleteIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
  Add,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import ClearIcon from "@mui/icons-material/Clear";
import Skeleton from "@mui/material/Skeleton";

import { Calendar } from "lucide-react";
import Key from "../../clientVariables.json";
import axios from "axios";
import { useSelector } from "react-redux";
import { userSelector } from "../user-redux/selector";

const Todolist = () => {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { user } = useSelector(userSelector);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [newTask, setNewTask] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Tasks");
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedTaskIndex, setHighlightedTaskIndex] = useState(-1);
  const [isGridView, setIsGridView] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [important, setImportant] = useState(false);
  const [sortOrder, setSortOrder] = useState("none");
  const [isEditing, setIsEditing] = useState(false);
  const theme = useTheme();

  // const sortedAndFilteredTasks = []

  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs")); // Very small mobile devices
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Mobile devices
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablets
  const isDesktopMedium = useMediaQuery(
    "(min-width: 1440px) and (max-width: 1919px)"
  );
  const isDesktopSmall = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1439px)"
  );
  const isDesktopExtraSmall = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );

  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewTask("");
    setTaskDescription("");
    setEditIndex(-1);
    setSelectedDate(null); // Clear selected date
    setImportant(false); // Clear important checkbox
    setIsEditing(false); // Reset editing state
  };

  useEffect(() => {
    getTasks();
  }, []);

  const getTasks = async ({ silent = false } = {}) => {
    if (!silent) setTasksLoading(true);
    try {
      const response = await axios.post(`${Key.domain}/task/get_TodoTasks`, {
        user_id: user?.id,
      });
      const data = response?.data?.data?.data;
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    } finally {
      if (!silent) setTasksLoading(false);
    }
  };

  const filteredTasks = tasks
    .filter((task) => {
      const searchLower = searchQuery.toLowerCase().trim();
      const titleMatch = task.title?.toLowerCase().includes(searchLower);
      const contentMatch = task.content?.toLowerCase().includes(searchLower);

      if (!searchLower) return true; // If no search query, show all tasks
      return titleMatch || contentMatch;
    })
    .filter((task) => {
      switch (selectedCategory) {
        case "All Tasks":
          return true;
        case "Important":
          return task.important === 1; // Only show important tasks
        case "Completed":
          return task.completed === 1;
        case "Incompleted":
          return task.completed === 0;
        default:
          return true;
      }
    });
  // Updated sorting logic
  const sortedAndFilteredTasks = [...filteredTasks].sort((a, b) => {
    if (sortOrder === "oldest") {
      return new Date(a.created) - new Date(b.created);
    }
    if (sortOrder === "latest") {
      return new Date(b.created) - new Date(a.created);
    }
    return 0;
  });

  const toggleCompletion = async (taskId) => {
    const selectedTask = tasks.find((task) => task.id === taskId);
    if (!selectedTask) return;

    const isCompleted = !selectedTask.completed;

    const updatedTask = {
      ...selectedTask,
      completed: isCompleted ? 1 : 0,
    };

    try {
      const response = await axios.put(
        `${Key.domain}/task/todo_task_update_`,
        updatedTask,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Update local state
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
        );

        // Show success snackbar
        const message = isCompleted
          ? `"${selectedTask.title}" marked as completed.`
          : `"${selectedTask.title}" marked as incomplete.`;
        setSnackbarMessage(message);
        setSnackbarSeverity("success");
        setShowSnackbar(true);
      } else {
        // Handle failure response
        console.error("Failed to toggle task completion:", response.statusText);
        setSnackbarMessage("Failed to update the task. Please try again.");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    } catch (error) {
      // Handle error case
      console.error(
        "Toggle Completion Error:",
        error.response?.data || error.message
      );
      setSnackbarMessage("An error occurred while updating the task.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const toggleImportance = async (taskId) => {
    // Find the task by ID
    const selectedTask = tasks.find((task) => task.id === taskId);
    if (!selectedTask) return;

    // Toggle the importance status
    const isMarkingImportant = !selectedTask.important;

    const updatedTask = {
      ...selectedTask,
      important: isMarkingImportant ? 1 : 0,
    };

    try {
      // Send the update request to the server
      const response = await axios.put(
        `${Key.domain}/task/todo_task_update_`,
        updatedTask,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Update local state to reflect the importance change
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
        );

        // Show success snackbar
        const message = isMarkingImportant
          ? `"${selectedTask.title}" marked as important.`
          : `"${selectedTask.title}" removed from important tasks.`;
        setSnackbarMessage(message);
        setSnackbarSeverity("success");
        setShowSnackbar(true);
      } else {
        // Handle failure response
        console.error("Failed to toggle task importance:", response.statusText);
        setSnackbarMessage("Failed to update the task. Please try again.");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    } catch (error) {
      // Handle error case
      console.error(
        "Toggle Importance Error:",
        error.response?.data || error.message
      );
      setSnackbarMessage("An error occurred while updating the task.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  // const deleteTask = async (index) => {
  //   const taskToDelete = tasks[index];

  //   try {
  //     // Send the DELETE request to the API with task_id
  //     const response = await axios.delete(
  //       `${Key.domain}/task/delete_created_Task`,
  //       {
  //         data: {
  //           task_id: taskToDelete.id, // Send task_id in the payload
  //         },
  //       }
  //     );

  //     // If the deletion is successful, remove the task from the UI by filtering out the deleted task
  //     if (response.status === 200) {
  //       setTasks(tasks?.filter((_, i) => i !== index)); // Remove the task from the state
  //       setSnackbarMessage("Task deleted successfully");
  //       setSnackbarSeverity("success");
  //       setShowSnackbar(true);
  //     } else {
  //       console.error("Failed to delete task");
  //       setSnackbarMessage("Failed to delete task");
  //       setSnackbarSeverity("error");
  //       setShowSnackbar(true);
  //     }
  //   } catch (error) {
  //     console.error("Error deleting task:", error);
  //     setSnackbarMessage("Error deleting task");
  //     setSnackbarSeverity("error");
  //     setShowSnackbar(true);
  //   }
  // };

  const deleteTask = async (taskId) => {
    try {
      // Send the DELETE request to the API with task_id
      const response = await axios.delete(
        `${Key.domain}/task/delete_created_Task`,
        {
          data: {
            task_id: taskId, // Use the actual task ID instead of array index
          },
        }
      );

      // If the deletion is successful, remove the task from the UI
      if (response.status === 200) {
        setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId)); // Filter by task ID
        setSnackbarMessage("Task deleted successfully");
        setSnackbarSeverity("success");
        setShowSnackbar(true);
      } else {
        console.error("Failed to delete task");
        setSnackbarMessage("Failed to delete task");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      setSnackbarMessage("Error deleting task");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };
  const addTask = async () => {
    if (!newTask.trim() || !selectedDate) {
      setSnackbarMessage("Task Title and Date are required!");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    try {
      const taskData = {
        title: newTask,
        user_id: user?.id,
        created: selectedDate,
        completed: 0,
        important: important ? 1 : 0,
        add_to_day: 0,
        folder_id: 0,
        remind: "2025-01-22T10:00:00",
        repeat_task: "weekly",
        due_date: "2025-01-23",
        planned: 0,
        content: taskDescription,
      };

      const response = await axios.post(
        `${Key.domain}/task/todo_task`,
        taskData
      );
      const data = response.data.data.data;

      if (data?.insertId > 0) {
        // After task creation, if the task is important, toggle the importance
        if (important) {
          toggleImportance(data.insertId); // Move task to the important list
        }

        getTasks({ silent: true }); // Refetch the task list
        handleDialogClose(); // Close the dialog

        setSnackbarMessage(`Task "${newTask}" added successfully.`);
        setSnackbarSeverity("success");
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error("An error occurred while adding the task:", error);
      setSnackbarMessage("An error occurred while adding the task.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const editTask = async () => {
    if (!newTask.trim() || !selectedDate) {
      setSnackbarMessage("Task Title and Date are required!");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    try {
      const taskData = {
        id: editIndex, // Unique task ID
        title: newTask,
        content: taskDescription,
        created: selectedDate,
        completed: 0,
        important: important ? 1 : 0,
        add_to_day: 0,
        folder_id: 0,
        remind: "2025-01-22T10:00:00",
        repeat_task: "weekly",
        due_date: "2025-01-23",
        planned: 0,
      };

      const response = await axios.put(
        `${Key.domain}/task/todo_task_update_`,
        taskData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log("Task updated successfully:", response.data);
        getTasks({ silent: true }); // Refresh tasks list
        handleDialogClose(); // Close dialog
        setSnackbarMessage("Task updated successfully!"); // Success message
        setSnackbarSeverity("success"); // Set snackbar severity to success
        setShowSnackbar(true); // Show snackbar
      } else {
        console.error("Failed to update the task:", response.statusText);
        setSnackbarMessage("Failed to update the task. Please try again.");
        setSnackbarSeverity("error");
        setShowSnackbar(true); // Show error snackbar
      }
    } catch (error) {
      console.error(
        "Update Task Error:",
        error.response?.data || error.message
      );
      setSnackbarMessage("An error occurred while updating the task.");
      setSnackbarSeverity("error");
      setShowSnackbar(true); // Show error snackbar
    }
  };

  const handleEdit = (task) => {
    setIsEditing(true); // Set editing state to true
    setEditIndex(task?.id); // Use a unique identifier for the task
    setNewTask(task?.title || ""); // Populate task title
    setTaskDescription(task?.content || ""); // Populate task description
    setSelectedDate(task?.created ? new Date(task.created) : null); // Populate date
    setImportant(task?.important === 1); // Convert importance to boolean
    handleDialogOpen(); // Open the dialog
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const categories = [
    { name: "All Tasks", icon: <ListIcon /> },
    { name: "Important", icon: <StarIcon /> },
    { name: "Completed", icon: <CompleteIcon /> },
    { name: "Incompleted", icon: <CalendarIcon /> },
  ];

  const pageHeight = "calc(100vh - 67px)";
  const today = new Date().toLocaleDateString();

  return (
    <Box
      sx={{
        mx: { xs: -1, md: -2 },
        px: 0.5,
        pt: 0.5,
        pb: 0,
        bgcolor: "#f4f7fb",
        height: pageHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Grid container spacing={0.5} sx={{ flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <Grid item xs={12} md={2.5} sx={{ height: "100%", display: "flex", minHeight: 0 }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              border: "1px solid #e3eaf3",
              p: 2,
              bgcolor: "#fff",
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={{
                mb: 2,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                height: "48px",
                borderRadius: "10px",
                boxShadow: "none",
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.35)",
                },
              }}
              onClick={handleDialogOpen}
            >
              <Add sx={{ fontSize: 20 }} />
              &nbsp; Add New Task
            </Button>
            <List sx={{ p: 0 }}>
              {categories.map((category) => (
                <ListItemButton
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  selected={selectedCategory === category.name}
                  sx={{
                    height: "46px",
                    mb: 0.5,
                    borderRadius: "10px",
                    "&.Mui-selected": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                      borderLeft: "4px solid #1976d2",
                      "&:hover": { bgcolor: "rgba(25, 118, 210, 0.12)" },
                    },
                  }}
                >
                  {category.icon}
                  <Typography sx={{ ml: 1, fontSize: "14px", fontWeight: 500 }}>
                    {category.name}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Task List */}
        <Grid item xs={12} md={9.5} sx={{ height: "100%", display: "flex", minHeight: 0 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              borderRadius: "12px",
              border: "1px solid #e3eaf3",
              p: 2,
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <TextField
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#90a4ae" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 320 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#64748b",
                  fontWeight: 500,
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                {today}
              </Typography>

              <FormControl size="small" sx={{ width: { xs: "100%", sm: 200 } }}>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ borderRadius: "10px", bgcolor: "#f8fafc" }}
                >
                  <MenuItem value="none">Sort by</MenuItem>
                  <MenuItem value="oldest">Nearing</MenuItem>
                  <MenuItem value="latest">Far-off</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              border: "1px solid #e3eaf3",
              bgcolor: "#fff",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #eef2f7",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                {selectedCategory}{" "}
                <Typography component="span" sx={{ color: "#64748b", fontWeight: 500 }}>
                  ({filteredTasks?.length})
                </Typography>
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  bgcolor: "#f1f5f9",
                  borderRadius: "10px",
                  p: 0.25,
                }}
              >
                <IconButton
                  onClick={() => setIsGridView(true)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    bgcolor: isGridView ? "#fff" : "transparent",
                    color: isGridView ? "#1976d2" : "#94a3b8",
                    boxShadow: isGridView ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  <GridViewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => setIsGridView(false)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    bgcolor: !isGridView ? "#fff" : "transparent",
                    color: !isGridView ? "#1976d2" : "#94a3b8",
                    boxShadow: !isGridView ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  <ListViewIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                p: 2,
              }}
            >
            <Grid container spacing={2}>
              {tasksLoading ? (
                Array.from({ length: isGridView ? 6 : 4 }).map((_, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={isGridView ? 4 : 12}
                    key={`task-skeleton-${index}`}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "12px",
                        p: 2,
                        border: "1px solid #e8edf3",
                        minHeight: isGridView ? 240 : 120,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#fff",
                      }}
                    >
                      <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="100%" />
                      <Skeleton variant="text" width="90%" />
                      <Skeleton variant="text" width="55%" sx={{ mb: 2 }} />
                      <Box sx={{ mt: "auto" }}>
                        <Skeleton variant="rounded" width={100} height={28} sx={{ mb: 1.5 }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Skeleton variant="rounded" width={90} height={30} />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Skeleton variant="circular" width={28} height={28} />
                            <Skeleton variant="circular" width={28} height={28} />
                            <Skeleton variant="circular" width={28} height={28} />
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))
              ) : sortedAndFilteredTasks?.length > 0 ? (
                sortedAndFilteredTasks.map((task, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={isGridView ? 4 : 12}
                    key={task.id ?? index}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "12px",
                        p: 2,
                        border: "1px solid #bfdbfe",
                        minHeight: isGridView ? 240 : 120,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: task.completed === 1 ? "#f8fafc" : "#fff",
                        borderLeft: Number(task.important) === 1
                          ? "4px solid #f472b6"
                          : "4px solid #60a5fa",
                        transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 24px rgba(59, 130, 246, 0.12)",
                          borderColor: "#93c5fd",
                          borderLeftColor: Number(task.important) === 1 ? "#ec4899" : "#3b82f6",
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textDecoration:
                            task.completed === 1 ? "line-through" : "none",
                          fontWeight: 600,
                          fontSize: "15px",
                          mb: 1,
                          color: task.completed === 1 ? "#94a3b8" : "#0f172a",
                          lineHeight: 1.4,
                        }}
                      >
                        {task.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          mb: 1.5,
                          color: "#64748b",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: isGridView ? 4 : 2,
                          overflow: "hidden",
                          lineHeight: 1.6,
                        }}
                      >
                        {task.content || "No description"}
                      </Typography>

                      {task.created && (
                        <Typography
                          variant="caption"
                          sx={{
                            mb: 1.5,
                            color: "#94a3b8",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <DateRangeOutlined sx={{ fontSize: 16 }} />
                          {new Date(task.created).toLocaleDateString()}
                        </Typography>
                      )}
                      <Divider sx={{ borderColor: "#eef2f7", mb: 1.5 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: "auto",
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={() => toggleCompletion(task.id)}
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: task.completed === 1 ? "#065f46" : "#92400e",
                            bgcolor:
                              task.completed === 1 ? "#d1fae5" : "#fef3c7",
                            borderRadius: "8px",
                            px: 1.5,
                            "&:hover": {
                              bgcolor:
                                task.completed === 1 ? "#a7f3d0" : "#fde68a",
                            },
                          }}
                        >
                          {task.completed === 1 ? "Completed" : "Incomplete"}
                        </Button>
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <IconButton
                            onClick={() => toggleImportance(task.id)}
                            size="small"
                            sx={{ color: task.important ? "#f43f5e" : "#94a3b8" }}
                          >
                            {task.important ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>

                          <IconButton
                            onClick={() => handleEdit(task)}
                            size="small"
                            sx={{ color: "#64748b" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => deleteTask(task.id)}
                            size="small"
                            sx={{ color: "#64748b", "&:hover": { color: "#ef4444" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    <Typography variant="body1">
                      No tasks found matching your search criteria
                    </Typography>
                  </Box>
                </Grid>
              )}
              {!tasksLoading && (
              <Grid item xs={12} sm={6} md={isGridView ? 4 : 12}>
                <Paper
                  elevation={0}
                  onClick={handleDialogOpen}
                  sx={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: "12px",
                    minHeight: isGridView ? 240 : 120,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    bgcolor: "#f8fafc",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#1976d2",
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <Add sx={{ fontSize: 28, color: "#94a3b8", mb: 0.5 }} />
                  <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                    Add new task
                  </Typography>
                </Paper>
              </Grid>
              )}
            </Grid>
            </Box>
          </Paper>
          </Box>
        </Grid>
      </Grid>
      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // Vertically align the title and icon
              justifyContent: "space-between", // Space between title and icon
            }}
          >
            <span>{editIndex === -1 ? "Add Task" : "Edit Task"}</span>
            <IconButton onClick={handleDialogClose}>
              {" "}
              {/* Add your close handler */}
              <ClearIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />
        <DialogContent>
          <TextField
            fullWidth
            label="Task Title"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            sx={{ marginBottom: 2 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={selectedDate}
              sx={{ width: "100%", marginBottom: 2 }}
              onChange={(newValue) => setSelectedDate(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={{
                    marginBottom: 2,
                    "& .MuiInputBase-root": {
                      width: "100%", // Ensure the input root takes the full width
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Description"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            multiline
            rows={3}
            sx={{ marginBottom: 2 }}
          />

          {/* Add Radio Button for Importance */}
          <FormControl sx={{ marginBottom: 2, marginTop: 2 }}>
            <FormLabel>Mark as Important</FormLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={important} // Bind to important state
                  onChange={() => setImportant(!important)} // Toggle onClick
                  color="primary"
                />
              }
              label="Important"
            />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDialogClose}
            sx={{
              backgroundColor: "#f44336", // Red color for Cancel
              color: "white",
              "&:hover": {
                backgroundColor: "#d32f2f", // Darker red on hover
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={isEditing ? editTask : addTask} // Ensure the correct function is called
            sx={{
              backgroundColor: isEditing ? "#388e3c" : "#1976D2", // Use different color for edit vs add
              color: "white",
              "&:hover": {
                backgroundColor: isEditing ? "#2c6e2f" : "#388e3c", // Darker color on hover
              },
            }}
          >
            {isEditing ? "Update" : "Add"}{" "}
            {/* Button text changes based on editing status */}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }} // Set to top-right
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Todolist;
